import type {
  Notification,
  NotificationActionWire,
  NotificationKind as ServerNotificationKind,
  NotificationMutationName
} from '@axilon-apps/domains-ontology-ui-state'
import type { AppContextEntry } from '../../chat-app-context'
import { runPageHref } from '../../lib/component-links'

/**
 * Presentation for a notification. Rows from the inbox-aware proxy carry
 * kind, bucket, link and actions; this module renders those as sent. Older
 * rows only have metadata, so the same shape is derived from it.
 */

export type NotificationBucket = 'action' | 'activity'

export type NotificationKind = ServerNotificationKind | 'connection'

export type NotificationMutation = NotificationMutationName

export type NotificationAction =
  | { kind: 'navigate'; label: string; href: string; primary?: boolean }
  | {
      kind: 'discuss'
      label: string
      prompt: string
      appContext: AppContextEntry[]
      primary?: boolean
    }
  | {
      kind: 'mutation'
      label: string
      mutation: NotificationMutation
      primary?: boolean
    }

export interface NotificationPresentation {
  kind: NotificationKind
  bucket: NotificationBucket
  kindLabel: string
  source: string
  title: string
  context?: string
  href?: string
  actions: NotificationAction[]
  groupKey: string
}

export interface PresentedNotification {
  notification: Notification
  /** Every notification folded into this row, newest first. */
  members: Notification[]
  presentation: NotificationPresentation
}

function str(
  metadata: Record<string, unknown> | null | undefined,
  key: string
): string | undefined {
  const value = metadata?.[key]
  return typeof value === 'string' && value !== '' ? value : undefined
}

function num(
  metadata: Record<string, unknown> | null | undefined,
  key: string
): number | undefined {
  const value = metadata?.[key]
  return typeof value === 'number' ? value : undefined
}

function stripPrefix(title: string, prefix: RegExp): string {
  return title.replace(prefix, '').trim()
}

function firstLine(text: string | null | undefined): string | undefined {
  const line = text?.split('\n').find((l) => l.trim() !== '')
  return line?.trim()
}

const KIND_LABELS: Record<NotificationKind, string> = {
  access_request: 'Access request',
  access_decided: 'Access decision',
  resource_shared: 'Shared with you',
  agent_finding: 'Agent finding',
  agent_run_failed: 'Agent run failed',
  agent_run_completed: 'Agent run',
  rule_fired: 'Rule fired',
  rule_cleared: 'Rule cleared',
  rule_test: 'Rule test',
  plc_change_review: 'PLC change',
  ingest_failed: 'Ingest failed',
  ingest_done: 'Ingest',
  ingest_question: 'Ingest question',
  ingest_paused: 'Ingest paused',
  ingest_review: 'Ingest review',
  diff_failed: 'Diff failed',
  diff_applied: 'Diff',
  mtc_disconnected: 'Connection',
  mtc_sequence_gap: 'Connection',
  mtc_model_changed: 'Device model',
  connection: 'Connection',
  legacy: 'Notification'
}

export function kindLabel(kind: NotificationKind): string {
  return KIND_LABELS[kind] ?? 'Notification'
}

function fromWire(action: NotificationActionWire): NotificationAction | null {
  const primary = action.primary ? { primary: true } : {}
  switch (action.kind) {
    case 'navigate':
      return action.href
        ? { kind: 'navigate', label: action.label, href: action.href, ...primary }
        : null
    case 'discuss':
      return action.prompt
        ? {
            kind: 'discuss',
            label: action.label,
            prompt: action.prompt,
            appContext: action.app_context ?? [],
            ...primary
          }
        : null
    case 'mutation':
      return action.mutation
        ? { kind: 'mutation', label: action.label, mutation: action.mutation, ...primary }
        : null
  }
}

/** Who or what a row is about, shown beside the kind label. */
function sourceFromMetadata(n: Notification): string {
  const md = n.metadata ?? undefined
  return (
    str(md, 'custom_agent_name') ??
    str(md, 'rule_name') ??
    str(md, 'controller_name') ??
    str(md, 'agent_url')?.replace(/^https?:\/\//, '') ??
    str(md, 'resourceName') ??
    ''
  )
}

function presentFromServer(
  n: Notification & { kind: NotificationKind; bucket: NotificationBucket }
): NotificationPresentation {
  return {
    kind: n.kind,
    bucket: n.bucket,
    kindLabel: kindLabel(n.kind),
    source: sourceFromMetadata(n),
    title: n.title,
    context: n.context ?? undefined,
    href: n.href ?? undefined,
    actions: (n.actions ?? []).map(fromWire).filter((a): a is NotificationAction => a !== null),
    groupKey: n.group_key ?? n.id
  }
}

export function presentNotification(n: Notification): NotificationPresentation {
  if (n.kind && n.bucket && n.kind !== 'legacy') {
    return presentFromServer({ ...n, kind: n.kind, bucket: n.bucket })
  }
  const md = n.metadata
  const source = str(md, 'source')
  const kind = str(md, 'kind')
  const dismiss: NotificationAction = {
    kind: 'mutation',
    label: 'Dismiss',
    mutation: 'dismiss'
  }

  if (kind === 'access_request') {
    const who = str(md, 'requester_name') ?? 'Someone'
    const resource = stripPrefix(n.title, /^Access requested for\s*/i)
    const message = str(md, 'message')
    return {
      kind: 'access_request',
      bucket: 'action',
      kindLabel: 'Access request',
      source: who,
      title: `${who} wants access to “${resource}”`,
      context: message ? `“${message}”` : undefined,
      href: str(md, 'resourceHref'),
      actions: [
        {
          kind: 'mutation',
          label: 'Grant viewer',
          mutation: 'grant_access_viewer',
          primary: true
        },
        ...(str(md, 'resourceType') === 'conversation'
          ? []
          : [
              {
                kind: 'mutation',
                label: 'Grant editor',
                mutation: 'grant_access_editor'
              } as const
            ]),
        { kind: 'mutation', label: 'Deny', mutation: 'deny_access' }
      ],
      groupKey: n.id
    }
  }

  if (source === 'custom_agent') {
    const agent = str(md, 'custom_agent_name') ?? 'Custom agent'
    const runId = str(md, 'run_id')
    const href = runId ? runPageHref('custom', runId) : undefined
    const failed = str(md, 'run_state') === 'failed'
    const headline = str(md, 'headline') ?? firstLine(n.summary) ?? n.title
    const openItems = num(md, 'open_action_items')
    const priority = str(md, 'top_priority')
    const appContext: AppContextEntry[] = [
      { key: 'Custom agent', value: agent },
      ...(runId ? [{ key: 'Custom agent run', value: runId }] : []),
      { key: 'Finding', value: headline }
    ]
    if (failed) {
      return {
        kind: 'agent_run_failed',
        bucket: 'action',
        kindLabel: 'Agent run failed',
        source: agent,
        title: headline,
        href,
        actions: [
          {
            kind: 'discuss',
            label: 'Discuss',
            primary: true,
            prompt: `The “${agent}” run failed with: ${headline}\nWhy did it fail, and how do I fix it?`,
            appContext
          },
          ...(href ? [{ kind: 'navigate', label: 'View run', href } as const] : []),
          dismiss
        ],
        groupKey: n.id
      }
    }
    const flagged = n.severity === 'warning' || n.severity === 'critical'
    if (!flagged) {
      return {
        kind: 'agent_run_completed',
        bucket: 'activity',
        kindLabel: 'Agent run',
        source: agent,
        title: `${agent} finished, nothing flagged`,
        href,
        actions: [],
        groupKey: n.id
      }
    }
    const itemsText =
      openItems !== undefined
        ? `${openItems} open action item${openItems === 1 ? '' : 's'}`
        : undefined
    const context = [
      itemsText,
      priority ? `${priority[0].toUpperCase()}${priority.slice(1)} priority` : undefined
    ]
      .filter(Boolean)
      .join(' · ')
    return {
      kind: 'agent_finding',
      bucket: 'action',
      kindLabel: 'Agent finding',
      source: agent,
      title: headline,
      context: context || undefined,
      href,
      actions: [
        {
          kind: 'discuss',
          label: 'Discuss',
          primary: true,
          prompt:
            `Let’s discuss the “${agent}” finding: ${headline.replace(/[.\s]+$/, '')}.` +
            (itemsText ? ` It has ${itemsText}.` : '') +
            ' Walk me through the evidence behind the root cause, and help me decide what to do about each action item.',
          appContext
        },
        ...(href ? [{ kind: 'navigate', label: 'View findings', href } as const] : []),
        ...(runId
          ? [
              {
                kind: 'mutation',
                label: 'Mark resolved',
                mutation: 'resolve_agent_run'
              } as const
            ]
          : [])
      ],
      groupKey: `custom_agent_run:${runId ?? n.id}`
    }
  }

  if (source === 'notification_rule') {
    const ruleId = str(md, 'notification_rule_id')
    const rule =
      str(md, 'rule_name') ?? stripPrefix(n.title, /^(Cleared:|Notification rule test:)\s*/i)
    const href = ruleId
      ? `/configure?type=notification-rules&id=${encodeURIComponent(ruleId)}`
      : undefined
    const event = str(md, 'trigger_event')
    if (event === 'cleared' || event === 'test') {
      return {
        kind: event === 'cleared' ? 'rule_cleared' : 'rule_test',
        bucket: 'activity',
        kindLabel: event === 'cleared' ? 'Rule cleared' : 'Rule test',
        source: rule,
        title:
          event === 'cleared'
            ? (firstLine(n.summary) ?? `${rule} cleared`)
            : `Test sent for ${rule}`,
        context: event === 'cleared' ? 'Closed the matching alert' : 'Only you see this',
        href,
        actions: [],
        groupKey: n.id
      }
    }
    return {
      kind: 'rule_fired',
      bucket: 'action',
      kindLabel: 'Rule fired',
      source: rule,
      title: firstLine(n.summary) ?? n.title,
      href,
      actions: [
        {
          kind: 'discuss',
          label: 'Discuss',
          primary: true,
          prompt: `The “${rule}” rule fired: ${firstLine(n.summary) ?? n.title}\nWhat’s going on, and is it a real problem?`,
          appContext: [{ key: 'Notification rule', value: rule }]
        },
        ...(href ? [{ kind: 'navigate', label: 'View rule', href } as const] : []),
        dismiss
      ],
      groupKey: `notification_rule:${ruleId ?? n.id}:${str(md, 'occurrence_id') ?? n.id}`
    }
  }

  if (source === 'mtcstream') {
    const agentUrl = str(md, 'agent_url') ?? 'MTConnect agent'
    const host = agentUrl.replace(/^https?:\/\//, '').replace(/:\d+$/, '')
    const event = str(md, 'event_id') ?? 'event'
    return {
      kind: 'connection',
      bucket: 'action',
      kindLabel: 'Connection',
      source: 'MTConnect',
      title: `Agent ${host} ${event === 'disconnect' ? 'disconnected' : n.title.toLowerCase()}`,
      context: agentUrl,
      href: '/settings?tab=connectivity',
      actions: [
        {
          kind: 'discuss',
          label: 'Discuss',
          primary: true,
          prompt: `MTConnect agent ${agentUrl} keeps disconnecting. What’s going on, and is it the network or the agent itself?`,
          appContext: [{ key: 'MTConnect agent', value: agentUrl }]
        },
        {
          kind: 'navigate',
          label: 'Connection health',
          href: '/settings?tab=connectivity'
        },
        dismiss
      ],
      groupKey: `mtc:${agentUrl}:${event}`
    }
  }

  if (kind === 'plc_autosync') {
    const controller =
      str(md, 'controller_name') ?? stripPrefix(n.title, /^PLC change detected:\s*/i)
    const branchId = str(md, 'branchId')
    const href = branchId ? `/branches/merge?source=${encodeURIComponent(branchId)}` : undefined
    return {
      kind: 'plc_change_review',
      bucket: 'action',
      kindLabel: 'PLC change',
      source: 'Autosync',
      title: `${controller} changed on the machine`,
      context: 'Review and merge the branch to update the ontology',
      href,
      actions: [
        ...(href
          ? [
              {
                kind: 'navigate',
                label: 'Review branch',
                href,
                primary: true
              } as const
            ]
          : []),
        dismiss
      ],
      groupKey: `plc_autosync:${str(md, 'controllerId') ?? n.id}`
    }
  }

  if (source === 'ingest' || source === 'diff') {
    const jobId = str(md, 'jobId')
    const href = jobId ? `/ingest?job=${encodeURIComponent(jobId)}` : '/ingest'
    const failed = n.severity === 'warning' || n.severity === 'critical'
    const file = n.title.split('—').slice(1).join('—').trim() || n.title
    const isDiff = source === 'diff'
    if (failed) {
      return {
        kind: isDiff ? 'diff_failed' : 'ingest_failed',
        bucket: 'action',
        kindLabel: isDiff ? 'Diff failed' : 'Ingest failed',
        source: file,
        title: n.title,
        context: firstLine(n.summary),
        href,
        actions: [{ kind: 'navigate', label: 'View job', href, primary: true }, dismiss],
        groupKey: n.id
      }
    }
    return {
      kind: isDiff ? 'diff_applied' : 'ingest_done',
      bucket: 'activity',
      kindLabel: isDiff ? 'Diff' : 'Ingest',
      source: file,
      title: isDiff ? `Diff applied from ${file}` : `${file} ingested`,
      context: firstLine(n.summary),
      href,
      actions: [],
      groupKey: n.id
    }
  }

  const important = n.severity === 'warning' || n.severity === 'critical'
  return {
    kind: 'legacy',
    bucket: important ? 'action' : 'activity',
    kindLabel: 'Notification',
    source: '',
    title: n.title,
    context: firstLine(n.summary),
    actions: important ? [dismiss] : [],
    groupKey: n.id
  }
}

/** Notifications that are pure receipts in the new model and never surface. */
export function isSuppressed(n: Notification): boolean {
  return (
    n.metadata?.source === 'ingest' && n.metadata?.kind === 'unstructured' && n.severity === 'info'
  )
}

/** Present and coalesce by group key, preserving newest-first order. */
export function presentNotifications(list: readonly Notification[]): PresentedNotification[] {
  // A rule's clear transition closes its activation: the cleared receipt stays
  // in Activity, the fired alert leaves Needs you.
  const clearedOccurrences = new Set(
    list
      .filter(
        (n) => n.metadata?.source === 'notification_rule' && n.metadata?.trigger_event === 'cleared'
      )
      .map((n) => n.metadata?.occurrence_id)
      .filter((id): id is string => typeof id === 'string')
  )
  const groups = new Map<string, PresentedNotification>()
  for (const n of list) {
    if (isSuppressed(n)) continue
    const occurrence = n.metadata?.occurrence_id
    if (
      n.metadata?.source === 'notification_rule' &&
      n.metadata?.trigger_event !== 'cleared' &&
      typeof occurrence === 'string' &&
      clearedOccurrences.has(occurrence)
    )
      continue
    const presentation = presentNotification(n)
    const existing = groups.get(presentation.groupKey)
    if (existing) {
      existing.members.push(n)
      continue
    }
    groups.set(presentation.groupKey, {
      notification: n,
      members: [n],
      presentation
    })
  }
  return [...groups.values()]
}
